import { queryClient } from '@/query-client';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import {
  type ConversationMessage,
  ConversationMessageSchema,
  type ConversationMessages,
  type ConversationMessagesQuery,
  ConversationMessagesSchema,
  type CreateConversationMessage,
  type DeleteManyConversationMessagesQuery,
} from '@mai/types';

import { apiClient } from './axios';

import { logger } from '@utils/logger';

export const CONVERSATION_MESSAGES_QUERY_KEY = 'conversation-messages';

export const useConversationMessagesQuery = (
  params: ConversationMessagesQuery,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: [CONVERSATION_MESSAGES_QUERY_KEY, params],
    queryFn: async () => {
      const response = await apiClient.get<ConversationMessages>(
        '/conversation-messages',
        { params },
      );
      const validatedData = ConversationMessagesSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate conversation messages data', {
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    enabled,
  });
};

export const useInfiniteConversationMessagesQuery = (
  params: ConversationMessagesQuery,
  enabled: boolean,
) => {
  return useInfiniteQuery({
    queryKey: [CONVERSATION_MESSAGES_QUERY_KEY, params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient.get<ConversationMessages>(
        '/conversation-messages',
        {
          params: { ...params, page: pageParam },
        },
      );
      const validatedData = ConversationMessagesSchema.safeParse(response.data);
      if (!validatedData.success) {
        logger.warn('failed to validate conversation messages data', {
          error: validatedData.error,
        });
        return response.data;
      }
      return validatedData.data;
    },
    getNextPageParam: (_, pages) => pages.length + 1,
    initialPageParam: 0,
    enabled,
  });
};

export const createConversationMessage = async (
  message: CreateConversationMessage,
) => {
  const response = await apiClient.post<ConversationMessage>(
    '/conversation-messages',
    message,
  );
  const validatedData = ConversationMessageSchema.safeParse(response.data);
  if (!validatedData.success) {
    logger.warn('failed to validate conversation message data', {
      error: validatedData.error,
    });
  }
  await queryClient.invalidateQueries({
    queryKey: [
      CONVERSATION_MESSAGES_QUERY_KEY,
      { conversationIds: [message.conversationId] },
    ],
  });
  return validatedData.data;
};

export const deleteConversationMessage = async (
  conversationMessageId: string,
) => {
  await apiClient.delete(`/conversation-messages/${conversationMessageId}`);
  await queryClient.invalidateQueries({
    queryKey: [CONVERSATION_MESSAGES_QUERY_KEY],
  });
};

export const deleteConversationMessages = async (
  params: DeleteManyConversationMessagesQuery,
) => {
  await apiClient.delete('/conversation-messages', {
    params,
  });
  await queryClient.invalidateQueries({
    queryKey: [CONVERSATION_MESSAGES_QUERY_KEY],
  });
};
